import { safeLocalStorage, type SafeStorage } from "../../utils/safeStorage";
/**
 * Runtime CSS-variable shim for app-wide dark mode.
 *
 * Emits `--mb-<path>` custom properties (light under :root, dark under
 * [data-mb-theme="dark"]) and rewrites every leaf of `rawColors` (local and the
 * ui-library's) to `var(--mb-…, <lightHex>)`, so the ~600 style files that read
 * static tokens follow the theme without edits. Must be the first import of the app.
 * Use `rawHex` where a literal colour is required (Vega, canvas, exports, maths).
 */
import { rawColors, rawHex } from "../tokens/colors";
import { darkRawColors } from "../tokens/darkRawColors";
import { homeColorVars } from "../tokens/home";
import { pageColorVars } from "../tokens/pageColorVars";
// Subpath export: loads the library's colour module without evaluating its components.
import { rawColors as libRawColors } from "@magicbi/ui-library/tokens/colors";

type Tree = { [key: string]: string | Tree };

export const STYLE_ID = "mb-color-vars";
const STORAGE_KEY = "magicbi-theme-mode";

/** `["text","highEmphasis"]` -> `--mb-text-high-emphasis`; `["grey","50"]` -> `--mb-grey-50`. */
export const toVarName = (path: string[]): string =>
  "--mb-" +
  path
    .map((p) => p.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase())
    .join("-");

const isGradient = (v: string): boolean => v.includes("gradient(");

const getAt = (tree: Tree | undefined, path: string[]): string | undefined => {
  let cur: string | Tree | undefined = tree;
  for (const k of path) {
    if (!cur || typeof cur === "string") return undefined;
    cur = cur[k];
  }
  return typeof cur === "string" ? cur : undefined;
};

const walk = (tree: Tree, cb: (path: string[], value: string, parent: Tree, key: string) => void, path: string[] = []) => {
  Object.keys(tree).forEach((key) => {
    const v = tree[key];
    if (typeof v === "string") cb([...path, key], v, tree, key);
    else walk(v, cb, [...path, key]);
  });
};

export interface ColorVarSources {
  /** Objects to mutate in place (default: local rawColors). */
  colors?: Tree;
  /** The ui-library rawColors, or null to skip (default: resolved from the package subpath). */
  lib?: Tree | null;
  light?: Tree;
  dark?: Tree;
}

let libOriginal: { target: Tree; light: Tree } | null = null;

const resolveLib = (): Tree | null => (libRawColors as unknown as Tree) ?? null;

export const buildColorVars = (sources: ColorVarSources = {}) => {
  const colors = sources.colors ?? (rawColors as unknown as Tree);
  const light = sources.light ?? (rawHex as unknown as Tree);
  const dark = sources.dark ?? (darkRawColors as unknown as Tree);
  const lightDecl: string[] = [];
  const darkDecl: string[] = [];
  const names: string[] = [];
  const mutations: Array<() => void> = [];

  const emit = (name: string, l: string, d: string) => {
    names.push(name);
    lightDecl.push(`${name}:${l};`);
    darkDecl.push(`${name}:${d};`);
  };

  walk(colors, (path, _v, parent, key) => {
    const l = getAt(light, path);
    if (l === undefined) return;
    const d = getAt(dark, path) ?? l;
    // Gradients with no dark counterpart stay literal.
    if (isGradient(l) && d === l) return;
    const name = toVarName(path);
    emit(name, l, d);
    mutations.push(() => {
      parent[key] = `var(${name}, ${l})`;
    });
  });

  const lib = sources.lib === undefined ? resolveLib() : sources.lib;
  if (lib) {
    if (!libOriginal || libOriginal.target !== lib) {
      libOriginal = { target: lib, light: JSON.parse(JSON.stringify(lib)) };
    }
    const libLight = libOriginal.light;
    walk(lib, (path, _v, parent, key) => {
      const l = getAt(libLight, path);
      if (l === undefined) return;
      const local = getAt(light, path);
      if (local === l) {
        // Same value as the local token: share its variable (emitted above unless a literal gradient).
        if (isGradient(l) && (getAt(dark, path) ?? l) === l) return;
        const name = toVarName(path);
        mutations.push(() => {
          parent[key] = `var(${name}, ${l})`;
        });
        return;
      }
      const d = getAt(dark, path);
      if (d === undefined || d === l) return;
      const name = toVarName(["lib", ...path]);
      emit(name, l, d);
      mutations.push(() => {
        parent[key] = `var(${name}, ${l})`;
      });
    });
  }

  // Colours that live outside rawColors (page-level tokens); emitted, never mutated.
  if (!sources.colors) {
    [homeColorVars, pageColorVars].forEach((vars) =>
      Object.entries(vars).forEach(([name, [l, d]]) => emit(name, l, d)),
    );
  }

  const css =
    `:root,[data-mb-theme="light"]{${lightDecl.join("")}}` +
    `[data-mb-theme="dark"]{${darkDecl.join("")}color-scheme:dark;}`;
  return { css, names, apply: () => mutations.forEach((m) => m()) };
};

const resolveMode = (storage: Pick<SafeStorage, "getItem"> | undefined, doc: Document): "light" | "dark" => {
  let stored: string | null = null;
  try {
    stored = storage?.getItem(STORAGE_KEY) ?? null;
  } catch {
    stored = null;
  }
  if (stored === "light" || stored === "dark") return stored;
  // "system" and unset both follow the OS (matches ThemeProvider with enableSystem).
  const mm = doc.defaultView?.matchMedia;
  return typeof mm === "function" && doc.defaultView!.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

export const installColorVars = (
  doc: Document = document,
  storage: Pick<SafeStorage, "getItem"> | undefined = safeLocalStorage,
  sources: ColorVarSources = {},
): void => {
  const { css, apply } = buildColorVars(sources);
  let style = doc.getElementById(STYLE_ID);
  if (!style) {
    style = doc.createElement("style");
    style.id = STYLE_ID;
    doc.head.appendChild(style);
  }
  style.textContent = css;
  doc.documentElement.dataset.mbTheme = resolveMode(storage, doc);
  apply();
};

// Jest never needs the shim; the app and Storybook load this module first.
if (typeof document !== "undefined" && process.env.NODE_ENV !== "test") {
  installColorVars();
}
